import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog } from '@material-ui/core'
import { useMutation, useQueryClient } from 'react-query'
import QueryKeys from '../../../utils/constants/queryKeys'
import inviteUsers from '../../../Apis/invitations/inviteUsers'
import USER_ORG_ROLES from '../../../utils/constants/userOrgRoles'
import INVITATION_TYPES from '../../../utils/constants/invitationTypes'
import { DUTIES, MANAGER, holdsDuty, isImpliedDuty, toGrants, toggledDuties } from '../../../utils/constants/permissionSets'
import CompetitionScopePicker from '../CompetitionScopePicker/CompetitionScopePicker'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Invite modal palette (from the Claude Design "Invite People" spec).
const ACCENT = '#dd5a4e'

const chipStyle = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '7px 12px',
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  border: '1px solid',
  userSelect: 'none',
  background: active ? '#fbedec' : '#fff',
  borderColor: active ? '#f2c0bc' : '#e3e0db',
  color: active ? '#b64d43' : '#6b6a67',
})

const boxStyle = (active) => ({
  width: 16,
  height: 16,
  borderRadius: 5,
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  border: active ? 'none' : '1.5px solid #cdcac3',
  background: active ? ACCENT : 'transparent',
  color: '#fff',
})

const makeEmptyRow = () => ({ email: '', role: USER_ORG_ROLES.MEMBER, duties: [], managerCompetitions: [] })

const dutyLabel = (id) => DUTIES.find((duty) => duty.id === id)?.label || id

/**
 * Invite people by email. Each invitee is a Member with at least one duty or, when the
 * caller is the Owner, an Admin. Ownership is passed from org settings, not here.
 */
const InviteDialog = ({ orgId, duties, competitions, invitableRoles, existingEmails, selfEmail, onClose }) => {
  const [rows, setRows] = useState([makeEmptyRow()])
  const [error, setError] = useState('')
  const [sentList, setSentList] = useState(null)
  const queryClient = useQueryClient()

  const inviteMutation = useMutation(QueryKeys.INVITE_USERS_TO_ORG, (data) => inviteUsers(data), {
    onSuccess: (_data, variables) => {
      setError('')
      setSentList(
        (variables?.invitations || []).map((inv) => ({
          email: inv.receiverEmail,
          summary:
            inv.role === USER_ORG_ROLES.MEMBER && inv.grants.length
              ? `${inv.role} · ${inv.grants.map((g) => dutyLabel(g.permissionSet)).join(' + ')}`
              : inv.role,
        })),
      )
      queryClient.invalidateQueries(QueryKeys.GET_ORG_PENDING_INVITATIONS)
    },
    onError: (err) => {
      setError(err?.response?.data?.details || err?.response?.data?.message || 'Failed to send invitation')
    },
  })

  const updateRow = (index, change) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...change } : row)))
    setError('')
  }

  const toggleDuty = (index, dutyId) => {
    const row = rows[index]
    const next = toggledDuties(row.duties, dutyId)
    updateRow(index, {
      duties: next,
      managerCompetitions: next.includes(MANAGER) ? row.managerCompetitions : [],
    })
  }

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()])
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index))

  const handleRowKeyDown = (e, index) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey && index === rows.length - 1)) {
      e.preventDefault()
      if (rows[index]?.email.trim()) {
        if (index === rows.length - 1) addRow()
        setTimeout(() => document.getElementById(`invite-row-${index + 1}`)?.focus(), 50)
      }
    }
  }

  const inviteMore = () => {
    setSentList(null)
    setError('')
    setRows([makeEmptyRow()])
  }

  const handleInvite = () => {
    setError('')
    const validRows = rows.filter((r) => r.email.trim())
    if (validRows.length === 0) return setError('Please enter at least one email address.')

    const invalidEmail = validRows.find((r) => !EMAIL_REGEX.test(r.email.trim()))
    if (invalidEmail) return setError(`Invalid email: ${invalidEmail.email}`)

    const selfRow = validRows.find((r) => r.email.trim().toLowerCase() === selfEmail)
    if (selfRow) return setError('You cannot invite yourself.')

    const existing = validRows.find((r) => existingEmails.includes(r.email.trim().toLowerCase()))
    if (existing) return setError(`${existing.email} is already in the organisation.`)

    const dutyless = validRows.find((r) => r.role === USER_ORG_ROLES.MEMBER && r.duties.length === 0)
    if (dutyless) return setError(`Pick at least one duty for ${dutyless.email.trim()}.`)

    inviteMutation.mutate({
      orgId,
      invitations: validRows.map((r) => ({
        receiverEmail: r.email.trim().toLowerCase(),
        role: r.role,
        type: INVITATION_TYPES.MEMBERSHIP,
        grants: r.role === USER_ORG_ROLES.MEMBER ? toGrants(r.duties, r.managerCompetitions) : [],
      })),
    })
  }

  const validRowCount = rows.filter((r) => EMAIL_REGEX.test(r.email.trim())).length
  const inviteDisabled = validRowCount === 0 || inviteMutation.isLoading
  const inviteLabel = validRowCount > 1 ? `Send ${validRowCount} invites` : 'Send invite'
  const footerNote =
    validRowCount === 0
      ? 'Enter at least one email to invite'
      : validRowCount > 1
      ? `${validRowCount} people ready to invite`
      : '1 person ready to invite'

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{ style: { backgroundColor: 'rgba(28,26,22,.42)' } }}
      PaperProps={{
        style: {
          width: '100%',
          maxWidth: 600,
          margin: 24,
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 24px 64px -12px rgba(28,26,22,.28), 0 2px 8px rgba(28,26,22,.08)',
        },
      }}
    >
      {sentList ? (
        <div style={{ padding: '40px 36px 36px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#eaf3ec', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='#3f8a57' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M20 6 9 17l-5-5' /></svg>
          </div>
          <div style={{ fontSize: 21, fontWeight: 700, color: '#26241f', marginBottom: 6 }}>
            {sentList.length > 1 ? `${sentList.length} invites sent` : 'Invite sent'}
          </div>
          <div style={{ fontSize: 15, color: '#8a877f', marginBottom: 24 }}>
            They&apos;ll get an email to join your workspace.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', marginBottom: 26 }}>
            {sentList.map((p) => (
              <div key={p.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#faf9f7', border: '1px solid #efedea', borderRadius: 11, padding: '12px 16px' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#3a3833', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</div>
                <div style={{ fontSize: 13, color: '#8a877f', flex: 'none' }}>{p.summary}</div>
              </div>
            ))}
          </div>
          <button type='button' onClick={inviteMore} style={{ width: '100%', border: 'none', background: '#26241f', color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, padding: 13, borderRadius: 11, cursor: 'pointer' }}>
            Invite more people
          </button>
        </div>
      ) : (
        <>
          <div style={{ padding: '26px 28px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: '#fdeeec', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke={ACCENT} strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><rect x='3' y='5' width='18' height='14' rx='2.5' /><path d='m3.5 7 8.5 6 8.5-6' /></svg>
                </div>
                <div>
                  <div style={{ fontSize: 19, fontWeight: 700, color: '#26241f', lineHeight: 1.2 }}>Invite people</div>
                  <div style={{ fontSize: 14, color: '#8a877f', marginTop: 2 }}>
                    Members see and do only what their duties cover; pick at least one.
                  </div>
                </div>
              </div>
              <button type='button' aria-label='Close' onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 6, margin: '-4px -6px 0 0', color: '#b5b2ab', borderRadius: 8 }}>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' aria-hidden='true'><path d='M18 6 6 18M6 6l12 12' /></svg>
              </button>
            </div>
          </div>

          <div style={{ padding: '0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((row, index) => (
              // Rows are edited in place and never reordered, so the index is a stable key.
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} style={{ background: '#faf9f7', border: '1px solid #efedea', borderRadius: 13, padding: '13px 13px 13px 15px', display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='#b5b2ab' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' style={{ flex: 'none' }} aria-hidden='true'><rect x='3' y='5' width='18' height='14' rx='2.5' /><path d='m3.5 7 8.5 6 8.5-6' /></svg>
                  <input
                    id={`invite-row-${index}`}
                    type='email'
                    aria-label='Email address'
                    value={row.email}
                    onChange={(e) => updateRow(index, { email: e.target.value })}
                    onKeyDown={(e) => handleRowKeyDown(e, index)}
                    placeholder='name@company.com'
                    autoFocus={index === 0}
                    style={{ flex: 1, border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, color: '#26241f', padding: '2px 0', minWidth: 0, outline: 'none' }}
                  />
                  <select
                    aria-label='Access'
                    value={row.role}
                    onChange={(e) => updateRow(index, { role: e.target.value })}
                    disabled={invitableRoles.length < 2}
                    style={{ flex: 'none', border: '1px solid #e3e0db', background: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: '#3a3833', padding: '7px 10px', borderRadius: 9, cursor: 'pointer' }}
                  >
                    {invitableRoles.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {rows.length > 1 && (
                    <button type='button' aria-label='Remove this person' onClick={() => removeRow(index)} style={{ flex: 'none', border: 'none', background: 'transparent', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#c2bfb8', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'><path d='M18 6 6 18M6 6l12 12' /></svg>
                    </button>
                  )}
                </div>
                {row.role === USER_ORG_ROLES.MEMBER ? (
                  duties.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, paddingLeft: 27, flexWrap: 'wrap' }}>
                      {duties.map((duty) => {
                        const active = holdsDuty(row.duties, duty.id)
                        // A Manager holds every duty: the others are ticked and change only with Manager.
                        const implied = isImpliedDuty(row.duties, duty.id)
                        return (
                          <button key={duty.id} type='button' aria-pressed={active} disabled={implied} title={implied ? 'Included with Manager' : undefined} onClick={() => toggleDuty(index, duty.id)} style={chipStyle(active)}>
                            <span style={boxStyle(active)}>{active ? '✓' : ''}</span>
                            {duty.label}
                          </button>
                        )
                      })}
                      {competitions?.length > 0 && row.duties.includes(MANAGER) && (
                        <CompetitionScopePicker
                          competitions={competitions}
                          selected={row.managerCompetitions}
                          label={`Competitions invitee ${index + 1} manages`}
                          onChange={(ids) => updateRow(index, { managerCompetitions: ids })}
                        />
                      )}
                    </div>
                  )
                ) : (
                  <div style={{ paddingLeft: 27, fontSize: 13, color: '#8a877f' }}>
                    Elevated access: everything except billing and org ownership
                  </div>
                )}
              </div>
            ))}

            <button type='button' onClick={addRow} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: ACCENT, cursor: 'pointer', padding: '6px 4px', marginTop: 2 }}>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' aria-hidden='true'><path d='M12 5v14M5 12h14' /></svg>
              Add another person
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '18px 28px 24px', marginTop: 6 }}>
            <div role='status' style={{ fontSize: 13, color: error ? ACCENT : '#a8a6a1', flex: 1, minWidth: 0 }}>
              {error || footerNote}
            </div>
            <div style={{ display: 'flex', gap: 10, flex: 'none' }}>
              <button type='button' onClick={onClose} style={{ border: '1px solid #e3e0db', background: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#3a3833', padding: '11px 18px', borderRadius: 11, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type='button' onClick={handleInvite} disabled={inviteDisabled} style={{ border: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, padding: '11px 20px', borderRadius: 11, cursor: inviteDisabled ? 'not-allowed' : 'pointer', background: inviteDisabled ? '#efc9c4' : ACCENT, color: '#fff' }}>
                {inviteMutation.isLoading ? 'Sending…' : inviteLabel}
              </button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  )
}

InviteDialog.propTypes = {
  orgId: PropTypes.string.isRequired,
  duties: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
  competitions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  invitableRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  existingEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  selfEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default InviteDialog
